import { usePage } from "@inertiajs/react"
import { HomeLine } from "@untitledui/icons"
import type { LucideIcon } from "lucide-react"
import { useMemo } from "react"

export interface BreadcrumbSegment {
  href?: string
  label: string
  icon?: LucideIcon | React.FC
}

// Inertia shares arbitrary resource props per page, so we treat the bag as
// opaque here and narrow individual reads below.
interface PageResourceProps {
  playlist?: { id?: string; title?: string }
  track?: { id?: string; post?: { title: string } }
}

const PATHS: Record<
  "home" | "allPlaylists" | "ownedPlaylists" | "showPlaylist" | "newPlaylist",
  string
> = {
  home: "/",
  allPlaylists: "/playlists",
  ownedPlaylists: "/playlists/owned",
  showPlaylist: "/playlists",
  newPlaylist: "/playlists/new",
}

const PATH_PATTERNS = {
  showPlaylist: /^\/playlists\/([^/]+)$/,
  editPlaylist: /^\/playlists\/([^/]+)\/edit$/,
  showTrack: /^\/playlists\/([^/]+)\/tracks\/(?!new$)([^/]+)$/,
  newTrack: /^\/playlists\/([^/]+)\/tracks\/new$/,
  editTrack: /^\/playlists\/([^/]+)\/tracks\/([^/]+)\/edit$/,
} as const

const isMyPlaylistsPath = (path: string) => path === PATHS.ownedPlaylists
const getSlug = (text: string) => text.toLowerCase().replace(/ /g, "-")

const getSecondSegmentByPath = (
  path = PATHS.allPlaylists,
): BreadcrumbSegment =>
  isMyPlaylistsPath(path)
    ? { href: PATHS.ownedPlaylists, label: "My Playlists" }
    : { href: PATHS.allPlaylists, label: "Playlists" }

const segmentItems = {
  first: {
    href: PATHS.home,
    label: "Home",
    icon: HomeLine,
  } satisfies BreadcrumbSegment,
  second: (path = PATHS.allPlaylists): BreadcrumbSegment =>
    getSecondSegmentByPath(path),
  third: (title = "Untitled Playlist", id?: string): BreadcrumbSegment => ({
    href: id ? `${PATHS.showPlaylist}/${id}` : `#${getSlug(title)}`,
    label: title,
  }),
  fourth: (title = "Untitled Track"): BreadcrumbSegment => ({
    href: title.toLowerCase().replace(/ /g, "-"),
    label: title,
  }),
} as const

export function useBreadcrumb(): BreadcrumbSegment[] {
  const usePageProps = usePage()
  const path = usePageProps.url
  const props = usePageProps.props as PageResourceProps

  return useMemo(() => {
    switch (true) {
      /**
       * All playlists path
       */
      case path === PATHS.allPlaylists:
        return [segmentItems.first, segmentItems.second(path)]

      /**
       * Owned playlists path
       */
      case path === PATHS.ownedPlaylists:
        return [segmentItems.first, segmentItems.second(path)]

      /**
       * New playlist path
       */
      case path === PATHS.newPlaylist:
        return [
          segmentItems.first,
          segmentItems.second(PATHS.ownedPlaylists), // TODO: Require change later when path history is implemented
          { label: "[ New Playlist ]" },
        ]

      /**
       * Show playlist path
       */
      case !!PATH_PATTERNS.showPlaylist.exec(path):
        return [
          segmentItems.first,
          segmentItems.second(path),
          segmentItems.third(props.playlist?.title, props.playlist?.id),
        ]

      /**
       * Edit playlist path
       */
      case !!PATH_PATTERNS.editPlaylist.exec(path):
        return [
          segmentItems.first,
          segmentItems.second(PATHS.ownedPlaylists), // TODO: Require change later when path history is implemented
          segmentItems.third(props.playlist?.title, props.playlist?.id),
        ]

      /**
       * Show track path
       */
      case !!PATH_PATTERNS.showTrack.exec(path):
        return [
          segmentItems.first,
          segmentItems.second(path),
          segmentItems.third(props.playlist?.title, props.playlist?.id),
          segmentItems.fourth(props.track?.post?.title),
        ]

      /**
       * New track path
       */
      case !!PATH_PATTERNS.newTrack.exec(path):
        return [
          segmentItems.first,
          segmentItems.second(path),
          segmentItems.third(props.playlist?.title, props.playlist?.id),
          { label: "[ New Track ]" },
        ]

      /**
       * Edit track path
       */
      case !!PATH_PATTERNS.editTrack.exec(path):
        return [
          segmentItems.first,
          segmentItems.second(path),
          segmentItems.third(props.playlist?.title, props.playlist?.id),
          segmentItems.fourth(props.track?.post?.title),
        ]

      /**
       * Root path as default
       */
      default:
        return [segmentItems.first]
    }
  }, [path, props])
}
